import axios from 'axios';
import { guestToken } from '../utils/guestOrders';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Session ID management
const getSessionId = () => {
  let sid = localStorage.getItem('zonalibre_session_id');
  if (!sid) {
    sid = crypto.randomUUID ? crypto.randomUUID() : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r = Math.random() * 16 | 0;
      return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
    });
    localStorage.setItem('zonalibre_session_id', sid);
  }
  return sid;
};

// Add auth token + session ID to every request
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('zl_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers['X-Session-ID'] = getSessionId();
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: async (userData) => {
    const response = await api.post('/auth/register', userData);
    if (response.data.token) {
      localStorage.setItem('zl_token', response.data.token);
    }
    return response.data;
  },

  login: async (credentials) => {
    const response = await api.post('/auth/login', credentials);
    if (response.data.token) {
      localStorage.setItem('zl_token', response.data.token);
    }
    return response.data;
  },

  getMe: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  updateRole: async (userId, role) => {
    const response = await api.put(`/auth/users/${userId}/role`, { role });
    return response.data;
  },

  updateStoreConfig: async (storeConfig) => {
    const response = await api.put('/auth/store-config', storeConfig);
    return response.data;
  },

  uploadProfileImage: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/auth/upload-profile-image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  getStoreInfo: async (sellerId) => {
    const response = await api.get(`/auth/store/${sellerId}`);
    return response.data;
  },

  // Addresses
  getAddresses: async () => {
    const response = await api.get('/auth/addresses');
    return response.data;
  },
  addAddress: async (addr) => {
    const response = await api.post('/auth/addresses', addr);
    return response.data;
  },
  updateAddress: async (id, addr) => {
    const response = await api.put(`/auth/addresses/${id}`, addr);
    return response.data;
  },
  deleteAddress: async (id) => {
    const response = await api.delete(`/auth/addresses/${id}`);
    return response.data;
  },

  logout: () => {
    localStorage.removeItem('zl_token');
    localStorage.removeItem('zl_user');
    localStorage.removeItem('zl_cart');
  },
};

// Products API
export const productsAPI = {
  getOffers: async (limit = 60) => (await api.get('/products/offers', { params: { limit } })).data,
  getAll: async (filters = {}) => {
    const response = await api.get('/products', { params: filters });
    return response.data;
  },

  getAllWithLocation: async (filters = {}, userLocation = null) => {
    const params = { ...filters };
    if (userLocation && userLocation.lat && userLocation.lng) {
      params.lat = userLocation.lat;
      params.lng = userLocation.lng;
    }
    const response = await api.get('/products', { params });
    return response.data;
  },

  search: async (q, userLocation = null, log = true) => {
    const params = { q, log };
    if (userLocation?.lat && userLocation?.lng) {
      params.lat = userLocation.lat;
      params.lng = userLocation.lng;
    }
    const response = await api.get('/products/search', { params });
    return response.data;
  },

  getById: async (productId) => {
    const response = await api.get(`/products/${productId}`);
    return response.data;
  },

  create: async (productData) => {
    const response = await api.post('/products', productData);
    return response.data;
  },

  update: async (productId, productData) => {
    const response = await api.put(`/products/${productId}`, productData);
    return response.data;
  },

  toggleActive: async (productId) => {
    const response = await api.put(`/products/${productId}/toggle-active`);
    return response.data;
  },

  delete: async (productId) => {
    const response = await api.delete(`/products/${productId}`);
    return response.data;
  },

  getBySeller: async (sellerId, includeInactive = false) => {
    const response = await api.get(`/products/seller/${sellerId}`, {
      params: { include_inactive: includeInactive }
    });
    return response.data;
  },

  uploadImage: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/products/upload-image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};

// Questions API
export const questionsAPI = {
  getByProduct: async (productId) => {
    const response = await api.get(`/questions/products/${productId}`);
    return response.data;
  },

  create: async (productId, question) => {
    const response = await api.post(`/questions/products/${productId}`, {
      question,
      product_id: productId,
    });
    return response.data;
  },

  answer: async (questionId, answer) => {
    const response = await api.put(`/questions/${questionId}/answer`, { answer });
    return response.data;
  },

  answerQuestion: async (questionId, answer) => {
    const response = await api.put(`/questions/${questionId}/answer`, { answer });
    return response.data;
  },

  getBySeller: async (sellerId, answered = null) => {
    const params = answered !== null ? { answered } : {};
    const response = await api.get(`/questions/seller/${sellerId}`, { params });
    return response.data;
  },

  getSellerQuestions: async () => {
    // Get current user from token and fetch their questions
    const userResponse = await api.get('/auth/me');
    const sellerId = userResponse.data._id || userResponse.data.id;
    const response = await api.get(`/questions/seller/${sellerId}`);
    return response.data;
  },
};

// Notifications API
export const notificationsAPI = {
  getAll: async () => {
    const response = await api.get('/notifications');
    return response.data;
  },

  markAsRead: async (notificationId) => {
    const response = await api.put(`/notifications/${notificationId}/read`);
    return response.data;
  },

  markAllAsRead: async () => {
    const response = await api.put('/notifications/read-all');
    return response.data;
  },

  delete: async (notificationId) => {
    const response = await api.delete(`/notifications/${notificationId}`);
    return response.data;
  },
};

// Orders API
export const ordersAPI = {
  checkAccount: async (email) => (await api.post('/orders/account-check', { email })).data,
  reportPayment: async (orderId) => (await api.post(`/orders/${orderId}/report-payment`, {}, { headers: { 'X-Guest-Token': guestToken(orderId) } })).data,
  getAll: async () => {
    const response = await api.get('/orders');
    return response.data;
  },

  getSales: async () => {
    const response = await api.get('/orders/sales');
    return response.data;
  },

  getById: async (orderId) => {
    const response = await api.get(`/orders/${orderId}`, { headers: { 'X-Guest-Token': guestToken(orderId) } });
    return response.data;
  },

  create: async (orderData) => {
    const response = await api.post('/orders', orderData);
    return response.data;
  },

  updateStatus: async (orderId, newStatus) => {
    const response = await api.put(`/orders/${orderId}/status?new_status=${newStatus}`);
    return response.data;
  },

  payWithCard: async (data) => {
    const response = await api.post('/orders/pay-card', data);
    return response.data;
  },

  payWithWallet: async (data) => {
    const response = await api.post('/orders/pay-wallet', data);
    return response.data;
  },

  checkout: async (data) => {
    const response = await api.post('/orders/checkout', data);
    return response.data;
  },

  syncCart: async (items, total) => {
    const response = await api.post('/orders/cart/sync', { items, total });
    return response.data;
  },
};

// Campaigns API
export const campaignsAPI = {
  getAll: async () => {
    const response = await api.get('/campaigns');
    return response.data;
  },

  getById: async (campaignId) => {
    const response = await api.get(`/campaigns/${campaignId}`);
    return response.data;
  },

  create: async (campaignData) => {
    const response = await api.post('/campaigns', campaignData);
    return response.data;
  },

  update: async (campaignId, campaignData) => {
    const response = await api.put(`/campaigns/${campaignId}`, campaignData);
    return response.data;
  },

  delete: async (campaignId) => {
    const response = await api.delete(`/campaigns/${campaignId}`);
    return response.data;
  },
};

// Ads API (Native Advertising)
export const adsAPI = {
  // Advertiser endpoints
  create: async (formData) => {
    const response = await api.post('/ads', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  getMyCampaigns: async (status = null) => {
    const params = status ? { status_filter: status } : {};
    const response = await api.get('/ads/my', { params });
    return response.data;
  },

  getMyStats: async () => {
    const response = await api.get('/ads/my/stats');
    return response.data;
  },

  update: async (campaignId, data) => {
    const response = await api.put(`/ads/${campaignId}`, data);
    return response.data;
  },

  delete: async (campaignId) => {
    const response = await api.delete(`/ads/${campaignId}`);
    return response.data;
  },

  // Feed consumption
  getAdsForFeed: async (params = {}) => {
    const response = await api.get('/ads/feed', { params });
    return response.data;
  },

  recordImpression: async (campaignId) => {
    const response = await api.post(`/ads/${campaignId}/impression`);
    return response.data;
  },

  recordClick: async (campaignId) => {
    const response = await api.post(`/ads/${campaignId}/click`);
    return response.data;
  },
  getImpressionsSeries: async (campaignId, start, end) => {
    const params = {};
    if (start) params.start = start;
    if (end) params.end = end;
    const response = await api.get(`/ads/${campaignId}/impressions-series`, { params });
    return response.data;
  },
};

// Search API
export const searchAPI = {
  autocomplete: async (query, lat = null, lng = null) => {
    const params = { q: query };
    if (lat) params.lat = lat;
    if (lng) params.lng = lng;
    const response = await api.get('/search/autocomplete', { params });
    return response.data;
  },
};

// Site Settings API
export const siteSettingsAPI = {
  get: async () => {
    const response = await api.get('/site-settings');
    return response.data;
  },
  update: async (data) => {
    const response = await adminApi.put('/site-settings', data);
    return response.data;
  },
};

// Payment Methods API
export const paymentMethodsAPI = {
  getAll: async () => {
    const response = await api.get('/payment-methods');
    return response.data;
  },
  toggle: async (methodId) => {
    const response = await adminApi.put(`/payment-methods/${methodId}/toggle`);
    return response.data;
  },
};

// Seller Stats API
export const sellerStatsAPI = {
  getEvolutionStats: async () => {
    const response = await api.get('/seller/evolution-stats');
    return response.data;
  },
};

// Admin API - uses separate token
const adminApi = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

adminApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers['X-Session-ID'] = getSessionId();
    return config;
  },
  (error) => Promise.reject(error)
);

export const adminAPI = {
  getReviews: async (params) => (await adminApi.get('/admin/reviews', { params })).data,
  moderateReview: async (id, status) => (await adminApi.patch(`/admin/reviews/${id}`, { status })).data,
  deleteReview: async (id) => (await adminApi.delete(`/admin/reviews/${id}`)).data,
  getExecutiveDashboard: async (days = 30) => (await adminApi.get('/analytics/dashboard', { params: { days } })).data,
  getModuleConfig: async (module) => (await adminApi.get(`/admin/modules/${module}/config`)).data,
  saveModuleConfig: async (module, config) => (await adminApi.put(`/admin/modules/${module}/config`, config)).data,
  listContent: async (kind) => (await adminApi.get(`/admin/content/${kind}`)).data,
  createContent: async (kind, data) => (await adminApi.post(`/admin/content/${kind}`, data)).data,
  updateContent: async (kind, id, data) => (await adminApi.put(`/admin/content/${kind}/${id}`, data)).data,
  deleteContent: async (kind, id) => (await adminApi.delete(`/admin/content/${kind}/${id}`)).data,
  getRequestsSummary: async () => (await adminApi.get('/admin/requests/summary')).data,
  exportCsv: async (entity) => (await adminApi.get(`/admin/export/${entity}`, { responseType: 'blob' })).data,
  login: async (credentials) => {
    const response = await adminApi.post('/admin/login', credentials);
    return response.data;
  },

  getDashboardStats: async () => {
    const response = await adminApi.get('/admin/dashboard/stats');
    return response.data;
  },

  // Users
  getUsers: async (params = {}) => {
    const response = await adminApi.get('/admin/users', { params });
    return response.data;
  },

  getUsersCount: async (params = {}) => {
    const response = await adminApi.get('/admin/users/count', { params });
    return response.data;
  },

  updateUserRole: async (userId, role) => {
    const response = await adminApi.put(`/admin/users/${userId}/role`, { role });
    return response.data;
  },

  deleteUser: async (userId) => {
    const response = await adminApi.delete(`/admin/users/${userId}`);
    return response.data;
  },

  // Products
  getProducts: async (params = {}) => {
    const response = await adminApi.get('/admin/products', { params });
    return response.data;
  },

  getProductsCount: async (params = {}) => {
    const response = await adminApi.get('/admin/products/count', { params });
    return response.data;
  },

  toggleProductActive: async (productId) => {
    const response = await adminApi.put(`/admin/products/${productId}/toggle-active`);
    return response.data;
  },

  deleteProduct: async (productId) => {
    const response = await adminApi.delete(`/admin/products/${productId}`);
    return response.data;
  },

  // Orders
  getOrders: async (params = {}) => {
    const response = await adminApi.get('/admin/orders', { params });
    return response.data;
  },

  getOrdersCount: async (params = {}) => {
    const response = await adminApi.get('/admin/orders/count', { params });
    return response.data;
  },

  updateOrderStatus: async (orderId, status) => {
    const response = await adminApi.put(`/admin/orders/${orderId}/status`, { status });
    return response.data;
  },

  // Notifications
  getNotifications: async () => {
    const response = await adminApi.get('/admin/notifications');
    return response.data;
  },

  // Setup
  setup: async () => {
    const response = await adminApi.post('/admin/setup');
    return response.data;
  },

  // Delivery Drivers Management
  getDeliveryDrivers: async (params = {}) => {
    const response = await adminApi.get('/admin/delivery-drivers', { params });
    return response.data;
  },

  getDeliveryDriversCount: async (params = {}) => {
    const response = await adminApi.get('/admin/delivery-drivers/count', { params });
    return response.data;
  },

  verifyDeliveryDriver: async (driverId, action, rejectionReason = null) => {
    const response = await adminApi.put(`/admin/delivery-drivers/${driverId}/verify`, {
      action,
      rejection_reason: rejectionReason
    });
    return response.data;
  },

  getDeliveryDriverDetails: async (driverId) => {
    const response = await adminApi.get(`/admin/delivery-drivers/${driverId}`);
    return response.data;
  },

  // Algorithms CRUD
  getHeisenbergExecutions: async () => (await adminApi.get('/admin/algorithms/motor_heisenberg/executions')).data,
  getAlgorithms: async () => {
    const response = await adminApi.get('/admin/algorithms');
    return response.data;
  },
  createAlgorithm: async (data) => {
    const response = await adminApi.post('/admin/algorithms', data);
    return response.data;
  },
  updateAlgorithm: async (id, data) => {
    const response = await adminApi.put(`/admin/algorithms/${id}`, data);
    return response.data;
  },
  deleteAlgorithm: async (id) => {
    const response = await adminApi.delete(`/admin/algorithms/${id}`);
    return response.data;
  },

  // Abandoned Carts
  getAbandonedCarts: async (params = {}) => {
    const response = await adminApi.get('/admin/abandoned-carts', { params });
    return response.data;
  },

  getAbandonedCartsCount: async () => {
    const response = await adminApi.get('/admin/abandoned-carts/count');
    return response.data;
  },

  // Bank / Central Bank
  getBankTransactions: async (params = {}) => {
    const response = await adminApi.get('/admin/bank/transactions', { params });
    return response.data;
  },
  getBankWithdrawals: async (params = {}) => {
    const response = await adminApi.get('/admin/bank/withdrawals', { params });
    return response.data;
  },
  getBankWallets: async () => {
    const response = await adminApi.get('/admin/bank/wallets');
    return response.data;
  },
  approveWithdrawal: async (txnId) => {
    const response = await adminApi.post(`/admin/bank/withdrawals/${txnId}/approve`);
    return response.data;
  },
  rejectWithdrawal: async (txnId) => {
    const response = await adminApi.post(`/admin/bank/withdrawals/${txnId}/reject`);
    return response.data;
  },
  adjustWallet: async (userId, amount, note) => {
    const response = await adminApi.post(`/admin/bank/wallets/${userId}/adjust`, { amount, note });
    return response.data;
  },
  getWalletDetail: async (userId) => (await adminApi.get(`/admin/bank/wallets/${userId}/transactions`)).data,
  getEscrow: async (status) => (await adminApi.get('/admin/bank/escrow', { params: status ? { status_filter: status } : {} })).data,
  releaseEscrow: async (txnId) => (await adminApi.post(`/admin/bank/escrow/${txnId}/release`)).data,
  refundEscrow: async (txnId) => (await adminApi.post(`/admin/bank/escrow/${txnId}/refund`)).data,

  // User suspension
  toggleSuspendUser: async (userId) => {
    const response = await adminApi.put(`/admin/users/${userId}/suspend`);
    return response.data;
  },

  // Dashboard timeseries
  getDashboardTimeseries: async (start, end) => {
    const params = {};
    if (start) params.start = start;
    if (end) params.end = end;
    const response = await adminApi.get('/admin/dashboard/timeseries', { params });
    return response.data;
  },

  // Ads admin toggle
  toggleAdStatus: async (campaignId) => {
    const response = await adminApi.put(`/admin/ads/${campaignId}/toggle-status`);
    return response.data;
  },

  // Intelligence
  getSessionTimeline: async (sessionId) => {
    const response = await adminApi.get(`/admin/intelligence/timeline/${sessionId}`);
    return response.data;
  },
  getSessions: async (limit = 50) => {
    const response = await adminApi.get('/admin/intelligence/sessions', { params: { limit } });
    return response.data;
  },
  getLiveMap: async () => {
    const response = await adminApi.get('/admin/live-map');
    return response.data;
  },
  getIntelligenceFunnel: async () => {
    const response = await adminApi.get('/admin/intelligence/funnel');
    return response.data;
  },

  // MP Config
  getMPConfig: async () => {
    const response = await adminApi.get('/admin/payments/config');
    return response.data;
  },
  updateMPConfig: async (data) => {
    const response = await adminApi.put('/admin/payments/config', data);
    return response.data;
  },

  // Order detail (Quantum Audit)
  getOrderDetail: async (orderId) => {
    const response = await adminApi.get(`/admin/orders/${orderId}/detail`);
    return response.data;
  },

  // P2P Payment Approval (triggers Heisenberg)
  approvePayment: async (orderId) => {
    const response = await adminApi.put(`/admin/orders/${orderId}/approve-payment`);
    return response.data;
  },

  // Upload shipping label PDF
  uploadShippingLabel: async (orderId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await adminApi.post(`/admin/orders/${orderId}/shipping-label`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  // Ad impressions series (admin)
  getAdImpressionsSeries: async (campaignId, start, end) => {
    const params = {};
    if (start) params.start = start;
    if (end) params.end = end;
    const response = await adminApi.get(`/ads/admin/${campaignId}/impressions-series`, { params });
    return response.data;
  },

  // Ads Management
  getPendingAds: async () => {
    const response = await adminApi.get('/ads/admin/pending');
    return response.data;
  },

  getAllAds: async (params = {}) => {
    const response = await adminApi.get('/ads/admin-all', { params });
    return response.data;
  },

  getAdsStats: async () => {
    const response = await adminApi.get('/ads/admin-stats');
    return response.data;
  },

  approveAd: async (campaignId) => {
    const response = await adminApi.put(`/ads/admin/${campaignId}/approve`);
    return response.data;
  },

  rejectAd: async (campaignId, reason) => {
    const response = await adminApi.put(`/ads/admin/${campaignId}/reject`, { reason });
    return response.data;
  },
};

// Delivery Driver API - uses separate token
const deliveryApi = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

deliveryApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('delivery_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

export const deliveryAPI = {
  register: async (data) => {
    const response = await deliveryApi.post('/delivery/register', data);
    return response.data;
  },

  login: async (credentials) => {
    const response = await deliveryApi.post('/delivery/login', credentials);
    return response.data;
  },

  getProfile: async () => {
    const response = await deliveryApi.get('/delivery/me');
    return response.data;
  },

  updateStatus: async (isOnline) => {
    const response = await deliveryApi.put('/delivery/status', { is_online: isOnline });
    return response.data;
  },

  updateLocation: async (lat, lng) => {
    const response = await deliveryApi.put('/delivery/location', { lat, lng });
    return response.data;
  },

  getAvailableOrders: async () => {
    const response = await deliveryApi.get('/delivery/orders/available');
    return response.data;
  },

  getMyDeliveries: async (status = null) => {
    const params = status ? { status_filter: status } : {};
    const response = await deliveryApi.get('/delivery/orders/my', { params });
    return response.data;
  },

  acceptOrder: async (orderId) => {
    const response = await deliveryApi.put(`/delivery/orders/${orderId}/accept`);
    return response.data;
  },

  updateDeliveryStatus: async (orderId, status) => {
    const response = await deliveryApi.put(`/delivery/orders/${orderId}/status`, { status });
    return response.data;
  },

  confirmPickup: async (orderId, pin) => {
    const response = await deliveryApi.post(`/delivery/orders/${orderId}/pickup`, { pin });
    return response.data;
  },

  confirmDelivery: async (orderId, pin) => {
    const response = await deliveryApi.post(`/delivery/orders/${orderId}/deliver`, { pin });
    return response.data;
  },

  getStats: async () => {
    const response = await deliveryApi.get('/delivery/stats');
    return response.data;
  },
};

export default api;

export const reviewsAPI = {
  list: async (id, page = 1) => (await api.get(`/reviews/products/${id}`, { params: { page } })).data,
  eligibility: async (id, orderId, token) => (await api.get(`/reviews/products/${id}/eligibility`, { params: { order_id: orderId || undefined }, headers: { 'X-Guest-Token': token || '' } })).data,
  create: async (id, data, token) => (await api.post(`/reviews/products/${id}`, data, { headers: { 'X-Guest-Token': token || '' } })).data,
};

// Tracking API
export const trackingAPI = {
  logEvent: async (action, metadata = {}) => {
    try {
      await api.post('/tracking/event', { action, metadata });
    } catch {
      // silent fail – tracking should never break UX
    }
  },
};

export { getSessionId };

// Favorites API
export const favoritesAPI = {
  toggle: async (productId) => {
    const response = await api.post('/favorites/toggle', { product_id: productId });
    return response.data;
  },
  getAll: async () => {
    const response = await api.get('/favorites');
    return response.data;
  },
  getIds: async () => {
    const response = await api.get('/favorites/ids');
    return response.data;
  },
};

// Wallet API (user)
export const walletAPI = {
  getWallet: async () => {
    const response = await api.get('/user/wallet');
    return response.data;
  },
  recharge: async (data) => {
    const response = await api.post('/user/wallet/recharge', data);
    return response.data;
  },
  updateBankAccount: async (data) => {
    const response = await api.put('/user/bank-account', data);
    return response.data;
  },
  withdraw: async (amount) => {
    const response = await api.post('/user/wallet/withdraw', { amount });
    return response.data;
  },
  getMPPublicKey: async () => {
    const response = await api.get('/payments/mp-public-key');
    return response.data;
  },
};
